import { useEffect, useId, useRef, useState } from "react";
import { subscribeLive, type LiveEvent } from "@/api/live";
import { useAudit } from "@/api/queries";
import styles from "./shell.module.css";

/**
 * The estate's pulse, drawn along the bottom edge of the top bar on every screen. It carries
 * on from the logo's own pulse: flat while nothing happens, a beat for each thing that does.
 * Time runs right to left over the last four hours; the right edge is now.
 */

type BeatKind = "alert" | "noise" | "step" | "heal" | "person";

interface Beat {
  id: string;
  at: number;
  kind: BeatKind;
  fresh: boolean;
}

// Four hours: long enough that a quiet afternoon still shows its last few beats.
const WINDOW_MS = 4 * 60 * 60 * 1000;
const HEIGHT = 18;
const BASE = HEIGHT - 1;

const SHAPE: Record<BeatKind, { h: number; ink: string }> = {
  alert: { h: 11, ink: "var(--crit)" },
  heal: { h: 9, ink: "var(--ok)" },
  person: { h: 8, ink: "var(--hum)" },
  step: { h: 5, ink: "var(--acc)" },
  noise: { h: 3, ink: "var(--dim2)" },
};

/** What an audit action or a live event means for the pulse, if anything. */
function beatOf(action: string, data?: Record<string, unknown>): BeatKind | null {
  switch (action) {
    case "incident.opened":
    case "signal.received":
    case "case.opened":
      return data?.status === "resolved" ? null : "alert";
    case "incident.noise":
      return "noise";
    case "flow_run.verified":
    case "ticket.resolved":
      return "heal";
    case "case.closed":
      return data?.by === "person" ? "person" : "heal";
    case "run.updated":
      return data?.status === "succeeded" ? "heal" : "step";
    case "flow_run.step":
    case "flow_run.started":
    case "flow_run.proposed":
    case "incident.classified":
    case "flow.drafted":
      return "step";
    case "case.handed_off":
    case "approval.requested":
    case "approval.created":
    case "approval.approved":
    case "approval.declined":
    case "approval.acknowledged":
    case "approval.paged":
    case "approval.expired":
      return "person";
    default:
      return null;
  }
}

function beatPath(x: number, h: number): string {
  // A small heartbeat complex: a dip, the spike, a deeper dip, back to the line.
  return [
    `M${x - 7} ${BASE}`,
    `L${x - 4} ${BASE}`,
    `L${x - 2.5} ${BASE + 1}`,
    `L${x} ${BASE - h}`,
    `L${x + 2.5} ${BASE + 1.5}`,
    `L${x + 4} ${BASE}`,
    `L${x + 7} ${BASE}`,
  ].join(" ");
}

export function PulseLine() {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [beats, setBeats] = useState<Beat[]>([]);
  const seeded = useRef(false);
  const history = useAudit(200);

  // The last twenty minutes from the audit log, once; live events add to it from then on.
  useEffect(() => {
    if (seeded.current || !history.data) return;
    seeded.current = true;
    const since = Date.now() - WINDOW_MS;
    const past: Beat[] = [];
    for (const e of history.data.events) {
      const at = Date.parse(e.occurred_at);
      const kind = beatOf(e.action);
      if (kind && at >= since) past.push({ id: `a${e.seq}`, at, kind, fresh: false });
    }
    setBeats((live) => [...past, ...live]);
  }, [history.data]);

  useEffect(
    () =>
      subscribeLive((e: LiveEvent) => {
        const kind = beatOf(e.kind, e.data);
        if (!kind) return;
        const at = Date.parse(e.at) || Date.now();
        setBeats((all) => [
          ...all.filter((b) => b.at >= Date.now() - WINDOW_MS),
          { id: `l${at}-${Math.random()}`, at, kind, fresh: true },
        ]);
      }),
    [],
  );

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 2000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const shown = beats.filter((b) => now - b.at <= WINDOW_MS);
  const xOf = (at: number) => width - 12 - ((now - at) / WINDOW_MS) * (width - 24);
  const counts = shown.reduce<Record<BeatKind, number>>(
    (acc, b) => ({ ...acc, [b.kind]: acc[b.kind] + 1 }),
    { alert: 0, noise: 0, step: 0, heal: 0, person: 0 },
  );
  const label =
    shown.length === 0
      ? "Pulse: nothing has happened in the last four hours"
      : `Pulse, last four hours: ${counts.alert} alerts, ${counts.heal} fixes verified, ${counts.person} handed to people, ${counts.step} steps run`;

  const gradient = `${useId()}-pulse`;
  return (
    <div ref={box} className={styles.pulse} role="img" aria-label={label}>
      {width > 0 ? (
        <svg width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`}>
          <defs>
            {/* The line gathers towards now, so the beats read as one monitor trace. In user
                space: a flat line has no height, so a bounding-box gradient would paint nothing. */}
            <linearGradient
              id={gradient}
              gradientUnits="userSpaceOnUse"
              x1={0}
              x2={width}
              y1={0}
              y2={0}
            >
              <stop offset="0" className={styles.pulseStopFar} />
              <stop offset="0.6" className={styles.pulseStopMid} />
              <stop offset="1" className={styles.pulseStopNow} />
            </linearGradient>
          </defs>
          <line
            x1={0}
            x2={width}
            y1={BASE}
            y2={BASE}
            className={styles.pulseBase}
            style={{ stroke: `url(#${gradient})` }}
          />
          {shown
            .sort((a, b) => SHAPE[a.kind].h - SHAPE[b.kind].h)
            .map((b) => (
              <path
                key={b.id}
                d={beatPath(xOf(b.at), SHAPE[b.kind].h)}
                className={b.fresh ? styles.pulseFresh : styles.pulseBeat}
                style={{ stroke: SHAPE[b.kind].ink }}
              />
            ))}
          <circle cx={width - 12} cy={BASE} r={5} className={styles.pulseNowHalo} />
          <circle cx={width - 12} cy={BASE} r={2.4} className={styles.pulseNow} />
        </svg>
      ) : null}
    </div>
  );
}
